// Copyright (c) Microsoft. All rights reserved.

import React, { createContext, useContext, ReactNode } from 'react';

interface AgentContextValue {
    selectedAgentId: string;
}

const AgentContext = createContext<AgentContextValue | undefined>(undefined);

interface AgentProviderProps {
    selectedAgentId: string;
    children: ReactNode;
}

/**
 * Provider component for agent context.
 * Makes selectedAgentId available to all nested components without prop drilling.
 */
export const AgentProvider: React.FC<AgentProviderProps> = ({ selectedAgentId, children }) => {
    return <AgentContext.Provider value={{ selectedAgentId }}>{children}</AgentContext.Provider>;
};

/**
 * Hook to access the agent context.
 * Must be used within an AgentProvider.
 * @returns The agent context value with selectedAgentId
 * @throws Error if used outside of AgentProvider
 */
export const useAgent = (): AgentContextValue => {
    const context = useContext(AgentContext);
    if (context === undefined) {
        throw new Error('useAgent must be used within an AgentProvider');
    }
    return context;
};

/**
 * Hook to safely access the agent context.
 * Returns undefined if used outside of AgentProvider.
 * @returns The agent context value or undefined
 */
export const useAgentSafe = (): AgentContextValue | undefined => {
    return useContext(AgentContext);
};
